import { index, modelOptions, mongoose, PropType, post, prop, type Ref } from "@typegoose/typegoose";
import { z } from "zod";
import { getZodSchemaFromClass, ObjectIdString, validate } from "../../zod-decorator/index.js";
import { IrrigationEvent } from "../irrigationEvent/irrigationEvent.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import type {
    IrrigationEventUpcomingActiveDatesFieldsWithDefaults,
    IrrigationEventUpcomingActiveDatesSchemaTypes,
} from "../types/irrigationEventUpcomingActiveDates.d.js";
import type { DtoValidatorTypes } from "../validatableClass.js";
import { Zone } from "../zone/zone.js";

const SCHEMA_KEY = "IrrigationEventUpcomingActiveDates" as const;

// The derived active-dates cache for an IrrigationEvent, held in its own collection so the
// event document can be versioned (SCD Type 2) without a daily cache refresh bumping a version.
// `irrigationEvent` stores the event's `_id` (the read key); `irrigationEventRecordId` holds the
// event's version-stable `recordId` for a later join on recordId.
@index({ irrigationEvent: 1 }, { unique: true })
@index({ datesActive: 1 })
@index({ irrigationEventRecordId: 1 }, { sparse: true })
@index({ zonesActive: 1 })
@modelOptions({
    schemaOptions: {
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        collection: "irrigationEventUpcomingActiveDates",
        timestamps: true,
        _id: true,
    },
})
@post<SchemaBase<IrrigationEventUpcomingActiveDates>>(...addSchemaKeyHook(SCHEMA_KEY))
export class IrrigationEventUpcomingActiveDates extends SchemaBase<
    IrrigationEventUpcomingActiveDates,
    IrrigationEventUpcomingActiveDatesFieldsWithDefaults
> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    public _id!: mongoose.Types.ObjectId;

    @validate({ zodValidator: ObjectIdString })
    @prop({ ref: () => IrrigationEvent, required: true })
    public irrigationEvent!: Ref<IrrigationEvent>;

    // Owning event's version-stable recordId (plain ObjectId, not a populatable _id ref).
    @prop({ type: () => mongoose.Types.ObjectId, required: false })
    public irrigationEventRecordId?: mongoose.Types.ObjectId;

    @validate({ zodValidator: z.array(z.string()), responseOnly: true })
    @prop({ type: () => String, required: true, default: [] }, PropType.ARRAY)
    public datesActive: string[] = [];

    // Denormalized copy of the event's zonesActive so the cache can be queried by zone directly.
    @validate({ zodValidator: z.array(ObjectIdString), responseOnly: true })
    @prop({ ref: () => Zone, required: true, default: [] }, PropType.ARRAY)
    public zonesActive: Ref<Zone>[] = [];

    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public _lastRefreshed?: Date;

    static getters = {} as const satisfies Record<string, (self: IrrigationEventUpcomingActiveDates) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<IrrigationEventUpcomingActiveDatesSchemaTypes>;

    constructor(
        properties?: SchemaConstructorArgs<
            IrrigationEventUpcomingActiveDates,
            IrrigationEventUpcomingActiveDatesFieldsWithDefaults
        >,
    ) {
        super(properties);
        Object.assign(this, properties);
    }
}

IrrigationEventUpcomingActiveDates.dtoValidators = getZodSchemaFromClass(
    IrrigationEventUpcomingActiveDates,
) as unknown as DtoValidatorTypes<IrrigationEventUpcomingActiveDatesSchemaTypes>;
